import { datadogRum } from "@datadog/browser-rum";

/**
 * Categorizes login RUM events so monitors can distinguish user friction
 * (e.g. wrong password) from genuine system failures (e.g. Cognito 5xx,
 * token refresh failure). Mirrors the worker-app pattern.
 *
 * Every login attempt — success or failure, across all flows (Cognito
 * password, Cognito magic link, Rippling SSO, token refresh, session
 * hydration) — should emit a RUM event tagged with one of the
 * `LOGIN_OUTCOME` values plus a discriminating `reason`.
 */

export const LOGIN_OUTCOME = {
  SUCCESS: "success",
  EXPECTED_ERROR: "expected_error",
  UNEXPECTED_ERROR: "unexpected_error",
} as const;

export type LoginOutcome = (typeof LOGIN_OUTCOME)[keyof typeof LOGIN_OUTCOME];

/**
 * Identity provider for the login attempt. Set as RUM global context at
 * flow entry so every subsequent RUM event in the session carries it.
 *
 * Monitors slice on this to compute per-IdP error rates.
 */
export const IDENTITY_PROVIDER = {
  COGNITO_PASSWORD: "cognito-password",
  COGNITO_MAGIC_LINK: "cognito-magic-link",
  RIPPLING_SSO: "rippling-sso",
} as const;

export type IdentityProvider = (typeof IDENTITY_PROVIDER)[keyof typeof IDENTITY_PROVIDER];

/**
 * Reason tags for error events. Cognito-mapped errors use the `auth/*`
 * code as-is for parity with existing monitor filters
 * (`@context.errorCode:"auth/..."`). Non-Cognito reasons use snake_case.
 */
export const LOGIN_REASON = {
  // Non-Cognito infrastructure reasons.
  NETWORK_FAILURE: "network_failure",
  TOKEN_REFRESH_FAILED: "token_refresh_failed",
  SESSION_HYDRATION_FAILED: "session_hydration_failed",
  COGNITO_SESSION_INIT_FAILED: "cognito_session_init_failed",
  ACCOUNT_LINKED_RETRY_EXHAUSTED: "account_linked_retry_exhausted",
  IDP_INITIATED_RETRY_EXHAUSTED: "idp_initiated_retry_exhausted",
  SSO_REDIRECT_FAILED: "sso_redirect_failed",
  SSO_CALLBACK_FAILED: "sso_callback_failed",
  // Catch-all when no mapped code is available.
  UNKNOWN: "unknown",
} as const;

export type LoginReason = string;

/**
 * Cognito error codes that represent user friction (wrong credentials,
 * expired link, rate-limited, etc.) — alerting on these would be noisy.
 *
 * Errors NOT in this set (auth/lambda-error, auth/unknown,
 * auth/network-request-failed) are treated as `unexpected_error` —
 * actual system problems worth paging on.
 *
 * NOTE: Keep in sync with the exclusion lists in `terraform/monitor.tf`
 * (`auth/user-not-found`, `auth/invalid-link`,
 * `auth/incorrect-credentials`).
 */
const EXPECTED_COGNITO_ERROR_CODES = new Set<string>([
  "auth/user-type-not-allowed",
  "auth/user-not-found",
  "auth/incorrect-password",
  "auth/incorrect-credentials",
  "auth/invalid-link",
  "auth/re-auth-required",
  "auth/too-many-requests",
  "auth/limit-exceeded",
  "auth/invalid-code",
  "auth/expired-code",
  "auth/password-reset-required",
  "auth/invalid-password",
]);

/**
 * Classifies a mapped Cognito error code into an `outcome` + `reason`
 * pair. Network errors are treated as `expected_error` to match the
 * existing terraform monitor exclusions (Network*Error* is filtered out
 * of system-error monitors).
 */
export function classifyCognitoError(errorCode: string): {
  outcome: LoginOutcome;
  reason: LoginReason;
} {
  if (errorCode === "auth/network-request-failed") {
    return {
      outcome: LOGIN_OUTCOME.EXPECTED_ERROR,
      reason: LOGIN_REASON.NETWORK_FAILURE,
    };
  }

  if (EXPECTED_COGNITO_ERROR_CODES.has(errorCode)) {
    return { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: errorCode };
  }

  // auth/lambda-error, auth/unknown, or any future code we haven't
  // explicitly classified — treat as unexpected so we get visibility.
  // Fall back to UNKNOWN for blank codes so the discriminator is never empty.
  return {
    outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
    reason: errorCode.trim() === "" ? LOGIN_REASON.UNKNOWN : errorCode,
  };
}

/**
 * Detects whether an unknown error looks like a network failure by name
 * or message, for paths that don't go through `mapCognitoError`.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const candidate = error as { name?: unknown; message?: unknown };
  const name = typeof candidate.name === "string" ? candidate.name : "";
  const message = typeof candidate.message === "string" ? candidate.message : "";
  return /network/i.test(name) || /network/i.test(message);
}

/**
 * Sets the identity provider on the RUM global context so every
 * subsequent RUM event in this session carries `@context.identityProvider`.
 *
 * Call this at the entry point of each login flow (password submit, magic
 * link request, SSO redirect, etc.). Pass `undefined` to clear, e.g. on
 * logout.
 */
/**
 * Metadata for the `GET_COGNITO_TOKEN_FAILURE` log emitted by the axios
 * request interceptor when token refresh fails. Lives here so api.ts stays
 * under its max-lines budget.
 */
export function tokenFailureMetadata(error: unknown): {
  source: string;
  outcome: LoginOutcome;
  reason: LoginReason;
} {
  return {
    source: "axios-interceptor",
    outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
    reason: isNetworkFailure(error)
      ? LOGIN_REASON.NETWORK_FAILURE
      : LOGIN_REASON.TOKEN_REFRESH_FAILED,
  };
}

export function setLoginIdentityProvider(provider: IdentityProvider | undefined): void {
  if (provider === undefined) {
    datadogRum.removeGlobalContextProperty("identityProvider");
    return;
  }
  datadogRum.setGlobalContextProperty("identityProvider", provider);
}
